import React, { useState, useEffect } from 'react';
import { cqmApi, aiRecommendationsApi, cqmDrillApi } from '../../lib/api';
import AINarrativePanel from '../../components/AINarrativePanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ShieldCheck, Target, TrendingUp, AlertTriangle, CheckCircle,
  ChevronRight, ArrowRight, Users, Lightbulb, RefreshCw,
  Heart, Activity, Pill, FileText, XCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import { toast } from 'sonner';

const MEASURE_CONFIG = {
  bp_control: { label: 'Blood Pressure Control', icon: Heart, color: '#991B1B', target: 70, benchmark: 65 },
  diabetes_control: { label: 'Diabetes Management (HbA1c)', icon: Activity, color: '#92400E', target: 75, benchmark: 60 },
  preventive_screening: { label: 'Preventive Screenings', icon: ShieldCheck, color: '#0E7490', target: 80, benchmark: 72 },
  medication_adherence: { label: 'Medication Adherence', icon: Pill, color: '#1A56DB', target: 85, benchmark: 78 },
};

export default function CQMDashboardPage() {
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillMeasure, setDrillMeasure] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cqmRes, recRes] = await Promise.all([
        cqmApi.getDashboard(),
        aiRecommendationsApi.get('cqm')
      ]);
      setData(cqmRes.data);
      setRecs(recRes.data.recommendations || []);
    } catch (err) {
      console.error('Failed to load CQM:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDrill = async (measureId) => {
    setDrillMeasure(measureId);
    setDrillLoading(true);
    try {
      const res = await cqmDrillApi.getMeasureDetail(measureId);
      setDrillData(res.data);
    } catch (err) {
      toast.error('Failed to load measure details');
    } finally {
      setDrillLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;

  const measures = data?.measures || [];
  const compositeScore = data?.composite_score || 0;
  const reimbursementImpact = data?.reimbursement_impact || {};

  // Create radial data for composite score
  const radialData = [{ name: 'MIPS Score', value: compositeScore, fill: compositeScore >= 75 ? '#166534' : compositeScore >= 50 ? '#92400E' : '#991B1B' }];

  // Measure bar data
  const measureBarData = measures.map(m => ({
    name: m.name?.split(':')[0]?.trim() || m.name,
    rate: m.performance_rate || 0,
    target: MEASURE_CONFIG[m.id]?.target || 75,
    benchmark: MEASURE_CONFIG[m.id]?.benchmark || 65,
  }));

  return (
    <div className="space-y-6" data-testid="cqm-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Clinical Quality Measures
          </h1>
          <p className="text-[#64748B] mt-1">HEDIS/MIPS quality metrics with actionable insights</p>
        </div>
        <Button variant="outline" onClick={loadData} data-testid="refresh-cqm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <AINarrativePanel scope="cqm" contextData={data} />

      {/* Composite Score + Reimbursement */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="careiq-card">
          <CardContent className="p-6 text-center">
            <div className="text-xs text-[#64748B] mb-2">MIPS Composite Score</div>
            <div className="relative mx-auto w-36 h-36">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={radialData} startAngle={180} endAngle={0}>
                  <RadialBar dataKey="value" cornerRadius={8} background={{ fill: '#F1F5F9' }} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <div>
                  <div className="text-3xl font-bold text-[#0A2342]">{compositeScore}</div>
                  <div className="text-[10px] text-[#64748B]">out of 100</div>
                </div>
              </div>
            </div>
            <div className={`text-sm font-medium mt-2 ${compositeScore >= 75 ? 'text-[#166534]' : compositeScore >= 50 ? 'text-[#92400E]' : 'text-[#991B1B]'}`}>
              {compositeScore >= 75 ? 'Exceptional' : compositeScore >= 50 ? 'Good' : 'Needs Improvement'}
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-card bg-gradient-to-br from-[#0A2342] to-[#0E7490] text-white">
          <CardContent className="p-6">
            <div className="text-sm opacity-80 mb-1">Reimbursement Impact</div>
            <div className="text-3xl font-bold mt-2">{reimbursementImpact.adjustment || '+1.5%'}</div>
            <div className="text-sm opacity-80 mt-1">Medicare payment adjustment</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="opacity-70">Estimated bonus:</span><span className="font-semibold">{reimbursementImpact.estimated_bonus || '$45,000'}</span></div>
              <div className="flex justify-between"><span className="opacity-70">vs. Penalty risk:</span><span className="font-semibold text-[#FCA5A5]">{reimbursementImpact.penalty_risk || '-$22,000'}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-card">
          <CardContent className="p-6">
            <div className="text-xs text-[#64748B] mb-3">Performance vs Benchmarks</div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={measureBarData} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" fontSize={9} stroke="#64748B" />
                <YAxis domain={[0, 100]} fontSize={10} stroke="#64748B" />
                <Tooltip />
                <Bar dataKey="rate" name="Your Rate" fill="#0E7490" radius={[4, 4, 0, 0]} />
                <Bar dataKey="benchmark" name="National Avg" fill="#E2E8F0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Individual Measures — Clickable */}
      <Card className="careiq-card" data-testid="measures-grid">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Target className="w-5 h-5 text-[#0E7490]" /> Quality Measures — Click to Drill Down
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(MEASURE_CONFIG).map(([id, cfg]) => {
              const measure = measures.find(m => m.id === id) || {};
              const rate = measure.performance_rate || 0;
              const meetsTarget = rate >= cfg.target;
              const Icon = cfg.icon;
              return (
                <div
                  key={id}
                  className="p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01]"
                  style={{ borderColor: meetsTarget ? '#166534' + '40' : cfg.color + '40' }}
                  onClick={() => openDrill(id)}
                  data-testid={`measure-${id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: cfg.color + '15' }}>
                        <Icon className="w-5 h-5" style={{ color: cfg.color }} />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-[#0A2342]">{cfg.label}</div>
                        <div className="text-[10px] text-[#64748B]">Target: {cfg.target}% · National: {cfg.benchmark}%</div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-end justify-between">
                      <div className="text-2xl font-bold text-[#0A2342]">{rate}%</div>
                      {meetsTarget ? (
                        <span className="text-xs text-[#166534] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> On Target</span>
                      ) : (
                        <span className="text-xs text-[#991B1B] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Below Target</span>
                      )}
                    </div>
                    <div className="w-full h-2 bg-[#F1F5F9] rounded-full mt-2">
                      <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: meetsTarget ? '#166534' : cfg.color }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
                      <span>{measure.numerator || 0}/{measure.denominator || 0} patients</span>
                      <span>Gap: {(measure.denominator || 0) - (measure.numerator || 0)} patients</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* AI Recommendations */}
      <Card className="careiq-card" data-testid="cqm-ai-recommendations">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#92400E]" /> AI-Powered Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recs.length > 0 ? (
            <div className="space-y-3">
              {recs.map((rec, i) => (
                <div key={i} className={`p-4 rounded-xl border-l-4 ${
                  rec.priority === 'high' ? 'border-l-[#991B1B] bg-[#991B1B]/5' :
                  rec.priority === 'critical' ? 'border-l-[#991B1B] bg-[#991B1B]/10' :
                  rec.priority === 'medium' ? 'border-l-[#92400E] bg-[#92400E]/5' :
                  'border-l-[#0E7490] bg-[#0E7490]/5'
                }`} data-testid={`rec-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                          rec.priority === 'high' || rec.priority === 'critical' ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                          rec.priority === 'medium' ? 'bg-[#92400E]/10 text-[#92400E]' : 'bg-[#0E7490]/10 text-[#0E7490]'
                        }`}>{rec.priority}</span>
                        <span className="text-[10px] text-[#64748B]">{rec.category}</span>
                      </div>
                      <div className="font-medium text-sm text-[#0A2342] mt-1">{rec.title}</div>
                      <p className="text-xs text-[#64748B] mt-1">{rec.description}</p>
                      {rec.actions && (
                        <div className="mt-2 space-y-1">
                          {rec.actions.map((a, j) => (
                            <div key={j} className="flex items-center gap-1 text-xs text-[#0E7490]">
                              <ArrowRight className="w-3 h-3 flex-shrink-0" /> {a}
                            </div>
                          ))}
                        </div>
                      )}
                      {rec.expected_impact && <div className="text-xs font-medium text-[#166534] mt-2">{rec.expected_impact}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#64748B]">No recommendations available</p>
          )}
        </CardContent>
      </Card>

      {/* Drill-down Dialog */}
      <Dialog open={!!drillMeasure} onOpenChange={() => { setDrillMeasure(null); setDrillData(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]" aria-describedby="drill-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillMeasure && MEASURE_CONFIG[drillMeasure] && (() => {
                const cfg = MEASURE_CONFIG[drillMeasure];
                const Icon = cfg.icon;
                return <><Icon className="w-5 h-5" style={{ color: cfg.color }} /> {cfg.label}</>;
              })()}
            </DialogTitle>
            <p id="drill-desc" className="text-sm text-[#64748B]">Patient-level compliance breakdown</p>
          </DialogHeader>
          {drillLoading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
          ) : drillData ? (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-2">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-[#F8FAFC] rounded-lg text-center">
                    <div className="text-2xl font-bold text-[#0A2342]">{drillData.compliance_rate}%</div>
                    <div className="text-[10px] text-[#64748B]">Compliance Rate</div>
                  </div>
                  <div className="p-3 bg-[#166534]/5 rounded-lg text-center">
                    <div className="text-2xl font-bold text-[#166534]">{drillData.compliant_count}</div>
                    <div className="text-[10px] text-[#64748B]">Compliant</div>
                  </div>
                  <div className="p-3 bg-[#991B1B]/5 rounded-lg text-center">
                    <div className="text-2xl font-bold text-[#991B1B]">{drillData.non_compliant_count}</div>
                    <div className="text-[10px] text-[#64748B]">Non-Compliant</div>
                  </div>
                </div>

                <Tabs defaultValue="non_compliant">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="non_compliant" className="text-sm">
                      <XCircle className="w-3 h-3 mr-1" /> Needs Action ({drillData.non_compliant_count})
                    </TabsTrigger>
                    <TabsTrigger value="compliant" className="text-sm">
                      <CheckCircle className="w-3 h-3 mr-1" /> Compliant ({drillData.compliant_count})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="non_compliant" className="mt-3">
                    {drillData.non_compliant_patients?.length > 0 ? (
                      <div className="space-y-2">
                        {drillData.non_compliant_patients.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-[#991B1B]/20 bg-[#991B1B]/5">
                            <div>
                              <div className="font-medium text-sm text-[#0A2342]">{p.name}</div>
                              <div className="text-[10px] text-[#64748B]">Risk: {p.risk_score} · DOB: {p.dob}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono text-sm font-medium text-[#991B1B]">{p.value}</div>
                              <div className="text-[10px] text-[#64748B]">{p.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-4">All patients are compliant</p>}
                  </TabsContent>
                  <TabsContent value="compliant" className="mt-3">
                    {drillData.compliant_patients?.length > 0 ? (
                      <div className="space-y-2">
                        {drillData.compliant_patients.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-[#166534]/20 bg-[#166534]/5">
                            <div>
                              <div className="font-medium text-sm text-[#0A2342]">{p.name}</div>
                              <div className="text-[10px] text-[#64748B]">Risk: {p.risk_score} · DOB: {p.dob}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono text-sm font-medium text-[#166534]">{p.value}</div>
                              <div className="text-[10px] text-[#64748B]">{p.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-4">No compliant patients yet</p>}
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
